import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { differenceInMinutes } from 'date-fns';
import { UtilsService } from '../Logic/utils.service';
import { environment } from '../../../environments/environment';
import { switchMap, map, forkJoin } from 'rxjs';

@Injectable()
export class LocationService {
  baseHref = `${environment.apiUrl}/location`;
  dawaUrl = `${environment.dawaUrl}/autocomplete`;
  dawaFallbackUrl = `${environment.dawaFallbackUrl}/autocomplete`;
  dawaLastFailed: Date | null = null;
  dawaDowntimeMinutesCheckInterval: number = 5;
  dawaRetries = 3;

  dawaDatavask = 'https://api.dataforsyningen.dk/datavask/adresser';
  dawaFindAddress = 'https://api.dataforsyningen.dk/adresser';

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private utilsService: UtilsService,
  ) {}

  async getAddressSuggestions(query: string) {
    const dawaUrl = `${this.dawaUrl}?q=${query}&fuzzy=true`;
    const fallbackUrl = `${this.dawaFallbackUrl}?q=${query}`;

    if (
      this.dawaLastFailed &&
      differenceInMinutes(new Date(), this.dawaLastFailed) <
        this.dawaDowntimeMinutesCheckInterval
    ) {
      // If DAWA service failed less than 5 minutes ago, use the fallback URL directly
      return this.fetchAddressSuggestions(fallbackUrl);
    }

    try {
      return await this.fetchAddressSuggestions(dawaUrl);
    } catch (dawaErr) {
      if (
        dawaErr instanceof HttpErrorResponse &&
        dawaErr.status >= 400 &&
        dawaErr.status < 500
      ) {
        return null;
      }
      this.dawaLastFailed = new Date();
      return this.fetchAddressSuggestions(fallbackUrl);
    }
  }

  private async fetchAddressSuggestions(url: string) {
    try {
      const response = await this.utilsService.fetchWithRetries(
        url,
        this.dawaRetries,
        {
          signal: AbortSignal.timeout(5000),
        },
      );
      return response;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  getGeolocation(id: string) {
    const url = `${this.baseHref}/geolocation/${id}`;
    return this.http.get<any>(url);
  }

  // @params - return list of suggestions for cities and regions
  async getRegionAndCitySuggestions(query: string) {
    const urlRegion = `https://dawa.aws.dk/regioner/autocomplete?q==${query}`;
    const urlKommune = `https://dawa.aws.dk/kommuner/autocomplete?q==${query}`;
    // const urlRegion = `${this.baseHref}/search/regioner?q=${query}`;
    // const urlKommune = `${this.baseHref}/search/kommuner?q=${query}`;
    const urlCity = `https://dawa.aws.dk/stednavne2/autocomplete?=hovedtype=Bebyggelse&undertype=by&fuzzy&q=${query}`;
    const regionResponseResp = await fetch(urlRegion);
    const kommuneResponseResp = await fetch(urlKommune);
    const cityResponse = await fetch(urlCity);

    const regionResponseData = await regionResponseResp.json();
    const kommuneResponseData = await kommuneResponseResp.json();
    const cityResponseData = await cityResponse.json();

    return regionResponseData.concat(kommuneResponseData, cityResponseData);
  }

  getAddressFromIPReverseGEOCoding(geoData) {
    // const url = `https://dawa.aws.dk/adgangsadresser/reverse?x=${geoData.lng}&y=${geoData.lat}`;
    const url = `${this.baseHref}/reverse-geo-coding/${geoData.lng}/${geoData.lat}`;
    return this.http.get<any>(url);
  }

  processAddressResult(result, tekst) {
    const address = {
      address: tekst,
      id: result.id,
      city: result.postnrnavn,
      zipCode: result.postnr,
      lat: result.y,
      lng: result.x,
    };
    return address;
  }

  searchAddress(manualAddressObject) {
    const query = Object.keys(manualAddressObject)
      .filter((key) => manualAddressObject[key] !== '')
      .map((key) => manualAddressObject[key])
      .join(' ');

    const url = `${this.dawaDatavask}?betegnelse=${query}`;

    return this.http.get(url).pipe(
      switchMap((data: any) => {
        const requests = data.resultater.map((result) =>
          this.http
            .get(`${this.dawaUrl}?id=${result.aktueladresse.id}`)
            .pipe(map((addressData: any) => addressData[0])),
        );
        return forkJoin(requests);
      }),
      map((addresses: any[]) =>
        addresses.filter((address) => address && address.tekst),
      ),
    );
  }
}
